import { useState } from "react";
import "./AninaTradeHomePage.css";
import "./AninaTradeHomePagePolish.css";
import { createStripeCheckoutSession } from "../billing/billingApi";

const platformHighlights = [
  {
    title: "Discover opportunities",
    description:
      "Use guided scanners, watchlists, market context, and AI research to understand what deserves attention.",
  },
  {
    title: "Plan before acting",
    description:
      "Review entries, stops, targets, risk, confirmations, and uncertainty before preparing an order.",
  },
  {
    title: "Manage one portfolio",
    description:
      "Portfolio is the single source of truth for manual paper trades and AI Paper AutoTrade activity.",
  },
  {
    title: "Learn continuously",
    description:
      "Use the Academy, journal, evidence history, and AI explanations to improve decisions over time.",
  },
];

const coreCapabilities = [
  {
    title: "Professional Live Trading",
    description:
      "A dedicated three-column workspace for configuration, chart analysis, trade planning, and active monitoring.",
  },
  {
    title: "AI Guidance",
    description:
      "Evidence-based explanations, scenario analysis, risk guidance, confirmation rules, and clear next-step support.",
  },
  {
    title: "Opportunity Discovery",
    description:
      "Scanner, watchlist, market intelligence, and future continuous monitoring capabilities coordinated by the AI Assistant.",
  },
  {
    title: "Unified Portfolio",
    description:
      "A broker-backed source of truth for paper and live positions, performance, and transaction history as account connections are enabled.",
  },
  {
    title: "Risk and Safety Controls",
    description:
      "Position sizing, stop planning, loss limits, broker-gated safeguards, permissions, and auditable actions.",
  },
  {
    title: "Learning and Improvement",
    description:
      "Academy lessons, journal review, decision history, and personalized insights that improve with responsible use.",
  },
];

const workflowSteps = [
  {
    number: "01",
    title: "Home",
    description: "Understand the company, platform mission, and safest place to begin.",
  },
  {
    number: "02",
    title: "Trade",
    description: "Open the professional workspace and study the chart and market context.",
  },
  {
    number: "03",
    title: "Ask AI Assistant",
    description: "Request evidence, risk guidance, scenarios, and a clearly explained next step.",
  },
  {
    number: "04",
    title: "Discover",
    description: "Use scanners and watchlists to find opportunities that match the strategy.",
  },
  {
    number: "05",
    title: "Manage",
    description: "Track broker-backed positions and performance through one Portfolio source of truth as account synchronization is enabled.",
  },
  {
    number: "06",
    title: "Learn",
    description: "Review decisions in the Journal and build skill through the Academy.",
  },
];

const philosophyPrinciples = [
  {
    title: "One page, one responsibility",
    description:
      "Every destination has a clear purpose so users do not have to search through duplicate tools or information.",
  },
  {
    title: "Always show the next step",
    description:
      "The platform guides users from discovery to planning, paper execution, portfolio management, and learning.",
  },
  {
    title: "Simplicity over complexity",
    description:
      "Professional capability should feel understandable, focused, and calm instead of crowded or confusing.",
  },
];

const safetyPrinciples = [
  "Paper-first, broker-gated development",
  "No automatic live brokerage execution",
  "Transparent risk and uncertainty",
  "Privacy-first personal memory",
  "Least-privilege AI agent permissions",
  "Auditable AI decisions and actions",
];

const latestUpdates = [
  {
    sequence: "01",
    status: "COMPLETED",
    title: "Guided Navigation V1",
    description:
      "The platform journey is organized around Home, Trade, Ask AI Assistant, Discover, Manage, and Learn.",
  },
  {
    sequence: "02",
    status: "COMPLETED",
    title: "Unified Portfolio & Insights",
    description:
      "Manual Paper Trading and AI Paper AutoTrade activity flow into one portfolio source of truth.",
  },
  {
    sequence: "03",
    status: "COMPLETED",
    title: "Professional Live Trading workspace",
    description:
      "Chart analysis, account context, quick trade planning, AI coaching, and market intelligence now share one dedicated workspace.",
  },
  {
    sequence: "04",
    status: "ACTIVE BUILD",
    title: "AninaTrade Home experience",
    description:
      "The company overview, mission, founder story, AI Assistant introduction, platform capabilities, trust, updates, and contact experience are being finalized.",
  },
];

function AninaTradeHomePage({ onNavigate }) {
  const billingReturnState = new URLSearchParams(
    window.location.search
  ).get("billing");
  const [billingInterval, setBillingInterval] = useState("monthly");

  const startCheckout = async (plan, interval) => {
    const result = await createStripeCheckoutSession(plan, interval);

    if (!result?.checkout_url) {
      throw new Error("Stripe Checkout URL is unavailable.");
    }

    window.location.assign(result.checkout_url);
  };

  return (
    <div className="aninaHome">
      {billingReturnState === "success" && (
        <p role="status">
          Subscription checkout completed. Your account access will update
          after the billing confirmation is processed.
        </p>
      )}

      {billingReturnState === "cancel" && (
        <p role="status">
          Subscription checkout was canceled. Your current plan remains
          unchanged.
        </p>
      )}

      <header className="aninaHome__topbar">
  <div className="aninaHome__brand">
    <span className="aninaHome__brandMark" aria-hidden="true">
  <svg
    width="58"
height="58"
    viewBox="0 0 32 32"
  >
    <path
      d="M15.8 3 5 25h5.2l7.9-16.2L15.8 3Z"
      fill="#00d8c9"
    />
    <path
      d="m20.2 8.2-8.4 16.9H17l5.7-11.6-2.5-5.3Z"
      fill="#00a99d"
    />
    <path
      d="m23.6 15.5-4.8 9.6H27l-3.4-9.6Z"
      fill="#00d8c9"
    />
  </svg>
</span>

    <div className="aninaHome__brandText">
      <strong>ANINATRADE</strong>
      <small>AI TRADING OPERATING SYSTEM</small>
    </div>
  </div>

  <nav className="aninaHome__nav" aria-label="AninaTrade home navigation">
    <button
      type="button"
      className="aninaHome__navActive"
      onClick={() => onNavigate("live")}
    >
      Trading Center
    </button>

    <button type="button" onClick={() => onNavigate("scanner")}>
      Discover
    </button>

    <button type="button" onClick={() => onNavigate("nina")}>
      Ask AI Assistant
    </button>

    <button type="button" onClick={() => onNavigate("academy")}>
      Academy
    </button>

    <button type="button" onClick={() => onNavigate("portfolio")}>
      Portfolio
    </button>

    <button
      type="button"
      className="aninaHome__pricingLink"
      onClick={() =>
        document
          .getElementById("pricing")
          ?.scrollIntoView({ behavior: "smooth", block: "start" })
      }
    >
      Pricing
    </button>
  </nav>

  <div className="aninaHome__accountActions">
    <span className="aninaHome__secureAccess">
      <span aria-hidden="true">♢</span>
      Secure Access
    </span>

   <button
  type="button"
  className="aninaHome__signIn"
  onClick={() => onNavigate("sign-in")}
>
  Sign In
</button>

    <button
      type="button"
      className="aninaHome__createAccount"
      onClick={() => onNavigate("create-account")}
    >
      Create Account
    </button>
  </div>
</header>
      <section className="aninaHome__hero" aria-labelledby="anina-home-title">
       <svg
  className="aninaHome__lightRibbon"
  viewBox="0 0 140 620"
  preserveAspectRatio="none"
  aria-hidden="true"
  style={{ display: "none" }}
>
  <defs>
    <filter id="aninaRibbonGlow" x="-100%" y="-20%" width="300%" height="140%">
      <feGaussianBlur stdDeviation="7" result="blur" />
      <feMerge>
        <feMergeNode in="blur" />
        <feMergeNode in="SourceGraphic" />
      </feMerge>
    </filter>
  </defs>

  <path
    d="M78 0 C30 90 112 150 48 235 C5 300 110 355 52 445 C18 500 96 555 62 620"
    fill="none"
    stroke="#67e8f9"
    strokeWidth="4"
    filter="url(#aninaRibbonGlow)"
  />

  <path
    d="M92 0 C46 92 124 158 62 242 C22 306 120 366 66 450 C34 505 108 558 78 620"
    fill="none"
    stroke="#22d3ee"
    strokeWidth="2.5"
    filter="url(#aninaRibbonGlow)"
  />

  <path
    d="M108 0 C64 96 132 165 78 250 C42 315 130 372 82 458 C52 512 118 565 94 620"
    fill="none"
    stroke="#8b5cf6"
    strokeWidth="3"
    filter="url(#aninaRibbonGlow)"
  />
</svg>
        <div className="aninaHome__heroCopy">


<h1 id="anina-home-title">
  Trade smarter.
  <br />
  Stay protected.
  <br />
  Perform with
<br />
<span className="aninaHome__confidenceText">confidence.</span>
</h1>

<p className="aninaHome__lead">
  AninaTrade combines AI-powered market intelligence, authenticated
  market data, AI guidance, smart scanners, disciplined risk planning,
  paper trading, portfolio management, and continuous learning in one
  professional trading workspace.
</p>

<div className="aninaHome__heroBenefits">
  <div>
    <span aria-hidden="true">✓</span>
    <p>
      <strong>Secure by design</strong>
      <small>Protected access, privacy-first architecture, and responsible controls.</small>
    </p>
  </div>

  <div>
    <span aria-hidden="true">✓</span>
    <p>
      <strong>AI guidance, human control</strong>
      <small>The AI Assistant analyzes the market while you remain in control of every decision.</small>
    </p>
  </div>

  <div>
    <span aria-hidden="true">✓</span>
    <p>
      <strong>Risk always first</strong>
      <small>Position sizing, stops, limits, and safeguards help protect your capital.</small>
    </p>
  </div>

  <div>
    <span aria-hidden="true">✓</span>
    <p>
      <strong>Practice. Learn. Improve.</strong>
      <small>Build skill through paper trading, education, journaling, and AI coaching.</small>
    </p>
  </div>
</div>

          <div className="aninaHome__actions">
  <button type="button" onClick={() => onNavigate("paper-trading")}>
    Start Paper Trading →
  </button>

  <button
    type="button"
    className="aninaHome__secondaryAction"
    onClick={() => onNavigate("live")}
  >
    Explore Trading Center
  </button>
</div>
        </div>

        <aside
  className="aninaHome__workspacePreview"
  aria-label="AninaTrade Trading Center preview"
>
  <div className="aninaHome__previewStats">
    <div>
     <span>MARKET TREND</span>
<strong>Bullish Bias</strong>
<small>Strengthening</small>
    </div>

    <div>
      <span>CONFIRMATION</span>
<strong>High</strong>
<small>Structure Aligned</small>
    </div>

    <div>
      <span>VOLATILITY</span>
<strong>Moderate</strong>
<small>Stable</small>
    </div>

<div>
  <span>CONFIDENCE</span>
  <strong>Elevated</strong>
  <small>Conditions Favorable</small>
</div>

</div>

  <div className="aninaHome__previewTrading">
    <div className="aninaHome__previewChart">
      <div className="aninaHome__previewSymbol">
        <div>
          <strong>ANINATRADE INTELLIGENCE</strong>
          <small>AI-guided market structure overview</small>
        </div>

        <span>AI-POWERED · AI ASSISTED</span>
      </div>

      <div className="aninaHome__previewChartCanvas" aria-hidden="true">
        <svg viewBox="0 0 640 250" preserveAspectRatio="none">
         <g aria-hidden="true">
  <line x1="35" y1="170" x2="35" y2="205" stroke="#22c55e" strokeWidth="2" />
  <rect x="29" y="178" width="12" height="18" rx="2" fill="#22c55e" />

  <line x1="72" y1="150" x2="72" y2="188" stroke="#22c55e" strokeWidth="2" />
  <rect x="66" y="158" width="12" height="22" rx="2" fill="#22c55e" />

  <line x1="109" y1="145" x2="109" y2="180" stroke="#ef4444" strokeWidth="2" />
  <rect x="103" y="151" width="12" height="20" rx="2" fill="#ef4444" />

  <line x1="146" y1="120" x2="146" y2="165" stroke="#22c55e" strokeWidth="2" />
  <rect x="140" y="130" width="12" height="25" rx="2" fill="#22c55e" />

  <line x1="183" y1="112" x2="183" y2="152" stroke="#22c55e" strokeWidth="2" />
  <rect x="177" y="119" width="12" height="24" rx="2" fill="#22c55e" />

  <line x1="220" y1="125" x2="220" y2="165" stroke="#ef4444" strokeWidth="2" />
  <rect x="214" y="132" width="12" height="24" rx="2" fill="#ef4444" />

  <line x1="257" y1="96" x2="257" y2="145" stroke="#22c55e" strokeWidth="2" />
  <rect x="251" y="106" width="12" height="28" rx="2" fill="#22c55e" />

  <line x1="294" y1="84" x2="294" y2="130" stroke="#22c55e" strokeWidth="2" />
  <rect x="288" y="94" width="12" height="27" rx="2" fill="#22c55e" />

  <line x1="331" y1="98" x2="331" y2="143" stroke="#ef4444" strokeWidth="2" />
  <rect x="325" y="107" width="12" height="26" rx="2" fill="#ef4444" />

  <line x1="368" y1="72" x2="368" y2="120" stroke="#22c55e" strokeWidth="2" />
  <rect x="362" y="82" width="12" height="28" rx="2" fill="#22c55e" />

  <line x1="405" y1="89" x2="405" y2="136" stroke="#ef4444" strokeWidth="2" />
  <rect x="399" y="99" width="12" height="27" rx="2" fill="#ef4444" />

  <line x1="442" y1="110" x2="442" y2="156" stroke="#ef4444" strokeWidth="2" />
  <rect x="436" y="119" width="12" height="27" rx="2" fill="#ef4444" />

  <line x1="479" y1="88" x2="479" y2="139" stroke="#22c55e" strokeWidth="2" />
  <rect x="473" y="99" width="12" height="29" rx="2" fill="#22c55e" />

  <line x1="516" y1="64" x2="516" y2="117" stroke="#22c55e" strokeWidth="2" />
  <rect x="510" y="75" width="12" height="31" rx="2" fill="#22c55e" />

  <line x1="553" y1="78" x2="553" y2="124" stroke="#ef4444" strokeWidth="2" />
  <rect x="547" y="87" width="12" height="27" rx="2" fill="#ef4444" />

  <line x1="590" y1="52" x2="590" y2="103" stroke="#22c55e" strokeWidth="2" />
  <rect x="584" y="63" width="12" height="29" rx="2" fill="#22c55e" />
</g>
<g opacity="0.42" aria-hidden="true">
  <rect x="24" y="220" width="14" height="22" rx="2" fill="#22c55e" />
  <rect x="58" y="214" width="14" height="28" rx="2" fill="#22c55e" />
  <rect x="92" y="224" width="14" height="18" rx="2" fill="#ef4444" />
  <rect x="126" y="207" width="14" height="35" rx="2" fill="#22c55e" />
  <rect x="160" y="201" width="14" height="41" rx="2" fill="#22c55e" />
  <rect x="194" y="216" width="14" height="26" rx="2" fill="#ef4444" />
  <rect x="228" y="195" width="14" height="47" rx="2" fill="#22c55e" />
  <rect x="262" y="205" width="14" height="37" rx="2" fill="#22c55e" />
  <rect x="296" y="218" width="14" height="24" rx="2" fill="#ef4444" />
  <rect x="330" y="191" width="14" height="51" rx="2" fill="#22c55e" />
  <rect x="364" y="209" width="14" height="33" rx="2" fill="#ef4444" />
  <rect x="398" y="215" width="14" height="27" rx="2" fill="#ef4444" />
  <rect x="432" y="198" width="14" height="44" rx="2" fill="#22c55e" />
  <rect x="466" y="204" width="14" height="38" rx="2" fill="#22c55e" />
  <rect x="500" y="219" width="14" height="23" rx="2" fill="#ef4444" />
  <rect x="534" y="193" width="14" height="49" rx="2" fill="#22c55e" />
  <rect x="568" y="210" width="14" height="32" rx="2" fill="#ef4444" />
  <rect x="602" y="187" width="14" height="55" rx="2" fill="#22c55e" />
</g>
          <polyline
            points="0,185 45,170 80,178 115,145 150,154 190,122 225,137 260,104 300,116 335,83 370,94 410,62 450,92 490,112 530,73 570,86 610,48 640,60"
            fill="none"
            stroke="currentColor"
            strokeWidth="4"
          />
          <polyline
  points="0,205 45,196 80,201 115,175 150,184 190,153 225,166 260,139 300,149 335,118 370,128 410,97 450,121 490,138 530,104 570,116 610,82 640,91"
  fill="none"
  stroke="#8b5cf6"
  strokeWidth="3"
/>
<polyline
  points="0,220 45,216 80,212 115,207 150,201 190,195 225,188 260,181 300,174 335,167 370,160 410,153 450,145 490,137 530,128 570,119 610,108 640,101"
  fill="none"
  stroke="#f59e0b"
  strokeWidth="2.5"
  strokeDasharray="8 6"
/>
        </svg>
      </div>

      <div className="aninaHome__previewOscillator" aria-hidden="true">
  <svg viewBox="0 0 640 90" preserveAspectRatio="none">
    <line
      x1="0"
      y1="45"
      x2="640"
      y2="45"
      stroke="rgba(148, 163, 184, 0.22)"
      strokeWidth="1"
      strokeDasharray="6 6"
    />

    <polyline
      points="0,58 40,43 80,49 120,31 160,37 200,55 240,61 280,45 320,29 360,35 400,57 440,63 480,47 520,28 560,34 600,22 640,30"
      fill="none"
      stroke="#22d3ee"
      strokeWidth="2.5"
    />

    <polyline
      points="0,64 40,55 80,58 120,44 160,48 200,60 240,65 280,56 320,43 360,46 400,61 440,66 480,58 520,44 560,47 600,38 640,42"
      fill="none"
      stroke="#8b5cf6"
      strokeWidth="2"
    />
  </svg>
</div>

      <div className="aninaHome__previewIndicators">
  <span>Trend</span>
  <span>Confirmation</span>
  <span>Risk</span>
</div>
    </div>

    <div className="aninaHome__previewNina">
      <span>TODAY'S OUTLOOK</span>
      <strong>Favorable</strong>
    <p>
  Market conditions support disciplined opportunity discovery.
</p>

<div className="aninaHome__previewInsights">
  <span>MARKET INSIGHTS</span>

  <ul>
    <li>Improving structure across key market conditions.</li>
    <li>Momentum building with supportive trend alignment.</li>
    <li>Risk management remains central to every decision.</li>
  </ul>
</div>

      <div className="aninaHome__previewPlan">
        <span>SMART TRADE PLAN</span>
        <strong>Bullish</strong>

        <dl>
          <div>
            <dt>Entry Zone</dt>
            <dd>Defined</dd>
          </div>
          <div>
            <dt>Stop Loss</dt>
            <dd>Managed</dd>
          </div>
          <div>
            <dt>Targets</dt>
            <dd>Defined</dd>
          </div>
        </dl>
      </div>
      <div className="aninaHome__previewRisk">
  <span>RISK OVERVIEW</span>

  <div>
    <strong>0.8%</strong>
    <small>Illustrative Account Risk</small>
  </div>

  <dl>
    <div>
      <dt>Max Daily Risk</dt>
      <dd>Defined</dd>
    </div>

    <div>
      <dt>Risk Status</dt>
      <dd>Healthy</dd>
    </div>
  </dl>
</div>
    </div>
  </div>
</aside>
      </section>

     <section
  className="aninaHome__featureStrip"
  aria-label="AninaTrade core platform advantages"
>
  <article className="aninaHome__featureCard">
    <span className="aninaHome__featureIcon" aria-hidden="true">AI</span>
    <div>
      <h3>AI Opportunity Intelligence</h3>
      <p>
        The AI Assistant scans market conditions to surface high-quality opportunities
        with context, evidence, and conviction.
      </p>
    </div>
  </article>

  <article className="aninaHome__featureCard">
    <span className="aninaHome__featureIcon" aria-hidden="true">R</span>
    <div>
      <h3>Advanced Risk Management</h3>
      <p>
        Position sizing, risk limits, stops, and real-time monitoring help
        keep capital protected.
      </p>
    </div>
  </article>

  <article className="aninaHome__featureCard">
    <span className="aninaHome__featureIcon" aria-hidden="true">P</span>
    <div>
      <h3>Paper Trading First</h3>
      <p>
        Practice in real market conditions with paper capital before risking
        live money.
      </p>
    </div>
  </article>

  <article className="aninaHome__featureCard">
    <span className="aninaHome__featureIcon" aria-hidden="true">L</span>
    <div>
      <h3>Learning Center</h3>
      <p>
        Academy lessons, journaling, and AI coaching support continuous
        improvement.
      </p>
    </div>
  </article>

  <article className="aninaHome__featureCard">
    <span className="aninaHome__featureIcon" aria-hidden="true">↔</span>
    <div>
      <h3>Cross-Device Continuity</h3>
      <p>
        Keep your AninaTrade workspace, portfolio, and learning experience
        connected across devices.
      </p>
    </div>
  </article>
</section>

<section
  id="pricing"
  className="aninaHome__pricing"
  aria-labelledby="anina-home-pricing-title"
>
  <div className="aninaHome__pricingHeading">
    <p className="aninaHome__eyebrow">PRICING &amp; PLANS</p>
    <h2 id="anina-home-pricing-title">
      Choose the AninaTrade plan that fits your journey
    </h2>
    <p>
      Four plans designed to grow with your trading journey.
      Secure subscription checkout is available through Stripe.
    </p>

    <div
      className="aninaHome__billingInterval"
      aria-label="Billing interval"
    >
      <button
        type="button"
        aria-pressed={billingInterval === "monthly"}
        onClick={() => setBillingInterval("monthly")}
      >
        Monthly
      </button>

      <button
        type="button"
        aria-pressed={billingInterval === "annual"}
        onClick={() => setBillingInterval("annual")}
      >
        Annual
      </button>
    </div>
  </div>

  <div className="aninaHome__pricingGrid">
    <article className="aninaHome__pricingCard">
      <h3>Free</h3>
      <p className="aninaHome__pricingPrice">$0</p>
      <p className="aninaHome__pricingBilling">Free plan</p>
      <p className="aninaHome__pricingTagline">Start &amp; Learn</p>
      <ul className="aninaHome__pricingFeatures">
        <li>Basic Market Scanner</li>
        <li>Paper Trading</li>
        <li>Watchlist</li>
        <li>Beginner Academy</li>
        <li>Journal</li>
        <li>Basic Portfolio tracking</li>
      </ul>
    </article>

    <article className="aninaHome__pricingCard">
      <span className="aninaHome__pricingBadge">Most Popular</span>
      <h3>AninaTrade Plus</h3>
      <p className="aninaHome__pricingPrice">
        $19.99<span>/month</span>
      </p>
      <p className="aninaHome__pricingBilling">About $199/year</p>
      <p className="aninaHome__pricingTagline">Trade with AI guidance</p>
      <ul className="aninaHome__pricingFeatures">
        <li>Everything in Free</li>
        <li>AI guidance</li>
        <li>Ranked market opportunities</li>
        <li>Entry, target &amp; stop guidance</li>
        <li>Position-sizing guidance</li>
        <li>Risk guidance</li>
      </ul>

      <button
        type="button"
        onClick={() => startCheckout("nina_plus", billingInterval)}
      >
        Choose AninaTrade Plus
      </button>
    </article>

    <article className="aninaHome__pricingCard">
      <h3>Pro</h3>
      <p className="aninaHome__pricingPrice">
        $49.99<span>/month</span>
      </p>
      <p className="aninaHome__pricingBilling">About $499/year</p>
      <p className="aninaHome__pricingTagline">For active traders</p>
      <ul className="aninaHome__pricingFeatures">
        <li>Everything in AninaTrade Plus</li>
        <li>Professional Trading Center</li>
        <li>Advanced scanner confirmations</li>
        <li>Advanced market intelligence</li>
        <li>Options intelligence &amp; practice</li>
        <li>Expanded portfolio and risk analysis</li>
      </ul>

      <button
        type="button"
        onClick={() => startCheckout("pro", billingInterval)}
      >
        Choose Pro
      </button>
    </article>

    <article className="aninaHome__pricingCard">
      <h3>Elite</h3>
      <p className="aninaHome__pricingPrice">
        $99.99<span>/month</span>
      </p>
      <p className="aninaHome__pricingBilling">About $999/year</p>
      <p className="aninaHome__pricingTagline">
        Maximum AninaTrade capability
      </p>
      <ul className="aninaHome__pricingFeatures">
        <li>Everything in Pro</li>
        <li>Premarket intelligence</li>
        <li>Regular-session intelligence</li>
        <li>After Hours intelligence</li>
        <li>Overnight intelligence</li>
        <li>Advanced AI opportunity discovery</li>
        <li>Premium market insights</li>
        <li>Paper AutoTrade</li>
        <li>AutoTrade capabilities as released</li>
        <li>Live AutoTrade when available and properly enabled</li>
        <li>Advanced automation controls</li>
        <li>Strongest automated risk safeguards</li>
        <li>Premium AI automation intelligence</li>
      </ul>

      <button
        type="button"
        onClick={() => startCheckout("elite", billingInterval)}
      >
        Choose Elite
      </button>
    </article>
  </div>
</section>
<section
  className="aninaHome__blueprint"
  aria-label="About AninaTrade"
>
  <p className="aninaHome__eyebrow">ABOUT ANINATRADE</p>
  <h2>Our company, leadership, mission, and purpose</h2>

  <div className="aninaHome__blueprintCards">
    <details className="aninaHome__blueprintCard">
      <summary>
        <span>Founder & Cofounder</span>
        <span aria-hidden="true">⌄</span>
      </summary>

      <div className="aninaHome__blueprintContent">
        <div>
          <strong>Aronde Amador</strong>
          <small>Founder — AninaTrade</small>
        </div>

        <div>
          <strong>Dennis Miller</strong>
          <small>Cofounder — AninaTrade</small>
        </div>
      </div>
    </details>

    <details className="aninaHome__blueprintCard">
      <summary>
        <span>Why We Created AninaTrade</span>
        <span aria-hidden="true">⌄</span>
      </summary>

      <div className="aninaHome__blueprintContent">
        <p>
          AninaTrade was created to make professional trading guidance easier
          to understand and to bring market intelligence, planning, risk
          management, paper trading, portfolio accountability, education, and
          AI guidance into one connected experience.
        </p>

        <p>
          The goal is not to remove the trader from the decision. The goal is
          to give the trader clearer information, disciplined tools, and a
          structured process for making better-informed decisions.
        </p>
      </div>
    </details>

    <details className="aninaHome__blueprintCard">
      <summary>
        <span>Our Mission</span>
        <span aria-hidden="true">⌄</span>
      </summary>

      <div className="aninaHome__blueprintContent">
        <p>
          Build a professional trading platform centered on clarity,
          discipline, education, risk awareness, and continuous improvement.
        </p>
      </div>
    </details>

    <details className="aninaHome__blueprintCard">
      <summary>
        <span>AI Assistant & Human Control</span>
        <span aria-hidden="true">⌄</span>
      </summary>

      <div className="aninaHome__blueprintContent">
        <p>
          The AI Assistant provides market intelligence, research, explanations,
          confirmation guidance, risk context, and coaching while keeping the
          user in control of trading decisions.
        </p>
      </div>
    </details>

    <details className="aninaHome__blueprintCard">
      <summary>
        <span>Safety & Risk Philosophy</span>
        <span aria-hidden="true">⌄</span>
      </summary>

      <div className="aninaHome__blueprintContent">
        <p>
          AninaTrade emphasizes paper trading, position sizing, planned stops,
          risk limits, transparent uncertainty, and responsible progression
          before greater trading responsibility is introduced.
        </p>
      </div>
    </details>

    <details className="aninaHome__blueprintCard">
      <summary>
        <span>About AninaTrade</span>
        <span aria-hidden="true">⌄</span>
      </summary>

      <div className="aninaHome__blueprintContent">
        <p>
          <strong>AninaTrade</strong>
          <br />
          AI Trading Operating System
          <br />
          Official website: AninaTrade.com
        </p>

        <p>
          AninaTrade is being built as an independent platform connecting
          market intelligence, the AI Assistant, trading workspaces, risk management,
          paper trading, portfolio management, and continuous learning.
        </p>
      </div>
    </details>
  </div>
</section>

      <p className="aninaHome__disclosure">
  AninaTrade provides market intelligence, trading-planning, paper-trading,
  risk-management, and educational tools. Trading involves risk. AI-generated
  insights do not guarantee outcomes, and users remain in control of their
  trading decisions.
</p>
    </div>
  );
}

export default AninaTradeHomePage;
